import {H4, HR} from "./Typography";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import {flavours} from "../theme";
import {Table, TBody} from "./Table";
import {Input} from "./Input";
import {SelectInput} from "./SelectInput";
import React, {useState} from "react";
import {css} from "glamor";
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import {LoadingError} from "./LoadingFork";

const ALIGN_LEFT = css({textAlign: 'left !important'})
const ALIGN_RIGHT = css({textAlign: 'right !important'})

/* Max raw extras image size we accept. Images upload directly to S3 via a
 * presigned URL (not through the GraphQL body), so this is purely a UX cap to
 * keep the public booking page images reasonably sized. */
const MAX_EXTRA_IMAGE_BYTES = 5 * 1024 * 1024
const MAX_EXTRA_IMAGE_LABEL = '5 MB'

const rateCardExtraOptions = [
    {
        label: "Parking",
        value: "PARKING"
    },
    {
        label: "Special Occasion",
        value: "OCCASION"
    },
    {
        label: "Alcohol",
        value: "ALCOHOL"
    },
    {
        label: "Checkin Extras",
        value: "CHECKIN"
    }
]

/*
 * Image is NOT uploaded here. On file pick we keep the File + a local preview
 * on the extra (_pendingFile / _previewUrl); the actual S3 upload happens on
 * form submit (see DetailsForm._onSubmit), so unsaved picks never hit S3.
 */

export function RateCardExtraContainer({rateCardExtras, setRateCardExtras, brandingKey}) {

    const updateRateCardExtras = (rateCardExtra, index) => {
        let newRateCardExtras = [...rateCardExtras];
        newRateCardExtras[index] = rateCardExtra;
        setRateCardExtras(newRateCardExtras);

    }
    const addRateCardExtra = () => {
        let newRateCardExtras = [...rateCardExtras, {description: "", amount: "0", type: "PARKING"}]
        setRateCardExtras(newRateCardExtras);
    }
    const removeRateCardExtra = (index) => {
        let newRateCardExtras = [...rateCardExtras];
        newRateCardExtras.splice(index, 1);
        setRateCardExtras(newRateCardExtras);
    }
    return <>
        <div style={{display: "flex", alignItems: "center"}}>
            <H4 style={{marginTop: 20}}>Extras/Offers</H4>
            <AddCircleOutlineIcon onClick={addRateCardExtra} style={{
                cursor: "pointer",
                fontSize: 30,
                marginLeft: 15,
                color: flavours.primary.highlightColor
            }}/>
        </div>
        <Table>
            <TBody>
                {rateCardExtras.map((c, i) => <RateCardExtraRow
                        key={`${i}-rateCardExtra`}
                        rateCardExtra={c}
                        index={i}
                        brandingKey={brandingKey}
                        setRateCardExtra={(newExtra, newExtraIndex) => updateRateCardExtras(newExtra, newExtraIndex)}
                        removeRateCardExtra={removeRateCardExtra}
                    />
                )}
            </TBody>
        </Table>
    </>
}

export function RateCardExtraRow({rateCardExtra, index, setRateCardExtra, removeRateCardExtra}) {

    const [uploadError, setUploadError] = useState(null);

    const updateRateCardExtra = (key, value) => {
        const newExtra = {...rateCardExtra};
        newExtra[key] = value;
        setRateCardExtra(newExtra, index);
    }

    const fileChangedHandler = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        if (file.size > MAX_EXTRA_IMAGE_BYTES) {
            setUploadError(`File size cannot be greater than ${MAX_EXTRA_IMAGE_LABEL}!`)
            return;
        }

        /* defer the S3 upload to form submit; keep the File + a local preview */
        setUploadError(null)
        setRateCardExtra(
            {...rateCardExtra, _pendingFile: file, _previewUrl: URL.createObjectURL(file)},
            index,
        )
    };

    return <>
        <tr>
            <td>
                <DeleteForeverIcon className={ALIGN_LEFT} onClick={() => removeRateCardExtra(index)} style={{
                    cursor: "pointer",
                    fontSize: 30,
                    marginLeft: 15,
                    color: flavours.danger.highlightColor
                }}/>
            </td>
            <td>

            </td>
        </tr>
        <tr>
            <td>
                <span className={ALIGN_LEFT}>Description</span>
            </td>
            <td>
                <Input className={ALIGN_RIGHT} value={rateCardExtra.description}
                       onValueChange={v => updateRateCardExtra("description", v)}/>
            </td>
        </tr>
        <tr>
            <td>
                <span className={ALIGN_LEFT}>Charge ($)</span>
            </td>
            <td>
                <Input className={ALIGN_RIGHT} value={rateCardExtra.amount} onValueChange={v => updateRateCardExtra("amount", v)}/>
            </td>
        </tr>
        <tr>
            <td>
                <span className={ALIGN_LEFT}>Category</span>
            </td>
            <td>
                <SelectInput
                    className={ALIGN_RIGHT}
                    options={rateCardExtraOptions}
                    value={rateCardExtra.type}
                    onChange={(e) => updateRateCardExtra("type", e.target.value)}
                />
            </td>
        </tr>
        <tr>
            <td>
                <span className={ALIGN_LEFT}>Daily Limit</span>
            </td>
            <td>
                <Input
                    className={ALIGN_RIGHT}
                    value={rateCardExtra.dailyLimit ?? ""}
                    onValueChange={v => {
                        /* digits only; blank = no limit (null) */
                        const digits = String(v).replace(/\D/g, "");
                        updateRateCardExtra("dailyLimit", digits === "" ? null : parseInt(digits, 10));
                    }}/>
            </td>
        </tr>
        <tr>
            <td>
                <span className={ALIGN_LEFT}>Disclaimer</span>
            </td>
            <td>
                <Input className={ALIGN_RIGHT} value={rateCardExtra.disclaimer}
                       onValueChange={v => updateRateCardExtra("disclaimer", v)}/>
            </td>
        </tr>
        <tr>
            <td>
                <span className={ALIGN_LEFT}>Image</span>
            </td>
            <td>
                <HR spaceAbove='medium' spaceBelow='medium'/>

                <input
                    type="file"
                    accept="image/*"
                    onChange={fileChangedHandler}
                />

                {(rateCardExtra._previewUrl || rateCardExtra.image) && (
                    <img
                        src={rateCardExtra._previewUrl || rateCardExtra.image}
                        alt="extra"
                        style={{display: 'block', maxHeight: 80, marginTop: 10}}
                    />
                )}

                {uploadError && <LoadingError error={uploadError}/>}

                <HR spaceAbove='medium' spaceBelow='medium'/>
            </td>
        </tr>
    </>
}
